import { newId, appendCooked, cookedEntry, type ReceptMutation } from '@familyhub/shared';
import { stamp } from '../platform/clock';
import { rdb, type RecipeRecord } from './db';
import { recipeSync } from './sync';

/**
 * The recepten write actions.
 *
 * Every change takes the same route as the other modules: apply it locally so the
 * screen updates at once, queue the mutation, and let the sync runner replay it.
 * These are full-row upserts (the agenda/todo pattern) — a recipe is edited by a
 * deliberate save, not a rapid-tap.
 */

/** The editable shape of a recipe — everything but the sync/identity columns. */
export interface RecipeInput {
  title: string;
  servings: number;
  total_minutes: number | null;
  ingredients: RecipeRecord['ingredients'];
  steps: RecipeRecord['steps'];
  tags: string[];
  source_type: RecipeRecord['source_type'];
  source_url: string | null;
  source_name: string | null;
  image_path: string | null;
  source_images: string[];
  notes: string | null;
}

async function writeRecipe(record: RecipeRecord): Promise<void> {
  const stamped: RecipeRecord = { ...record, updated_at: stamp(), deleted_at: record.deleted_at ?? null };
  const mutation = { op: 'upsert', table: 'recepten', id: newId(), row: stamped } as unknown as ReceptMutation;
  await rdb.transaction('rw', [rdb.recepten, rdb.outbox], async () => {
    await rdb.recepten.put(stamped);
    await rdb.outbox.add({ mutation, queued_at: new Date().toISOString() });
  });
  void recipeSync.sync();
}

/** Save a brand-new recipe (manual entry, or a reviewed import draft). Returns its id. */
export async function addRecipe(input: RecipeInput, createdBy: string | null): Promise<string> {
  const id = newId();
  await writeRecipe({
    id,
    ...input,
    created_by: createdBy,
    // The household-facing "date added": a wall clock stamped now, client-side, so
    // it's right and available offline (distinct from the server-set created_at).
    added_at: new Date().toISOString(),
    cooked_log: [],
    pinned: false,
    updated_at: '', // stamped in writeRecipe
    deleted_at: null,
  });
  return id;
}

/**
 * Pin (or unpin) a recipe — a pinned recipe floats to the top of the overview.
 * A full-row LWW write like every other recipe edit; `...existing` keeps the rest.
 */
export async function setPinned(id: string, pinned: boolean): Promise<void> {
  const existing = await rdb.recepten.get(id);
  if (!existing) return;
  await writeRecipe({ ...existing, pinned });
}

/**
 * Record that the recipe was cooked, now — appends a `{at, by}` entry to its cook
 * log (capped in shared/log.ts). Called from the cook-mode end prompt and the
 * manual "Gekookt vandaag" button. A full-row LWW write, like every recipe edit.
 */
export async function logCooked(id: string, by: string | null): Promise<void> {
  const existing = await rdb.recepten.get(id);
  if (!existing) return;
  await writeRecipe({ ...existing, cooked_log: appendCooked(existing.cooked_log, cookedEntry(by)) });
}

/** Edit an existing recipe's fields. */
export async function updateRecipe(id: string, patch: Partial<RecipeInput>): Promise<void> {
  const existing = await rdb.recepten.get(id);
  if (!existing) return;
  await writeRecipe({ ...existing, ...patch });
}

/** Soft delete — the tombstone propagates to the other phone. */
export async function deleteRecipe(id: string): Promise<void> {
  const updated_at = stamp();
  const mutation = {
    op: 'delete',
    table: 'recepten',
    id: newId(),
    row: { id, updated_at },
  } as unknown as ReceptMutation;
  await rdb.transaction('rw', [rdb.recepten, rdb.outbox], async () => {
    const existing = await rdb.recepten.get(id);
    if (existing) {
      await rdb.recepten.put({ ...existing, deleted_at: existing.deleted_at ?? updated_at, updated_at });
    }
    await rdb.outbox.add({ mutation, queued_at: new Date().toISOString() });
  });
  void recipeSync.sync();
}
